/**
 * Evidence Domain Definition (domain-def@4) — the spine.
 *
 * The graded per-user claim ledger every generated sentence back-references.
 * Re-derivation (domain-model.md §3.1): the ledger moves onto ITS OWN stream
 * (`…::Evidence`), decoupling claim ordering from profile revisions (fixes
 * critique 6); claims reference profile/CV/assets by URN, not by stream
 * co-location. **The Evidence stream is the ONLY home of claim bodies** —
 * Workshop and Ingest reference claims by URN (§3.0 rule 1).
 *
 * The `EvidenceClaim` / `EvidenceRef` value objects live in
 * `career-primitives.ts` (cited by Cv, Profile, Match, Application). This
 * definition declares the per-user ledger resource and the claim lifecycle events.
 *
 * Stream: `Agg::career::{userId}::Evidence`.
 */

import type { DomainDefinitionJSON } from "./schema"

const evidenceDomain = {
  schemaVersion: "domain-def@4",
  name: "Evidence",
  version: "1.0.0",
  description:
    "Evidence Ledger — the graded, per-user spine of claims (evidenced / self-positioning / gap) that every application sentence back-references; the only home of claim bodies",

  types: {
    /** The per-user evidence ledger — projection over the claim lifecycle events */
    EvidenceLedger: {
      "+": "reject",
      owner: "UserId",
      claims: "EvidenceClaim[]",
    },

    // ─── Event payloads ──────────────────────────────────────────

    /** The canonical claim write: a graded claim with sources + provenance
     *  pointing at its producer (ingest run URN / workshop turn URN). */
    ClaimProposedPayload: {
      "+": "reject",
      owner: "UserId",
      claim: "EvidenceClaim",
      /** Provenance of the producer — every proposed claim points at where it came from. */
      prov: "ProvenanceBundle",
    },

    /** The USER confirms a proposed claim → nothing becomes evidenced without this (law C5). */
    ClaimConfirmedPayload: {
      "+": "reject",
      owner: "UserId",
      claimId: "Urn",
      confirmedAt: "Timestamp",
    },

    /** A user revision — full replacement snapshot; grade re-derived (may downgrade, never silently upgrade). */
    ClaimEditedPayload: {
      "+": "reject",
      owner: "UserId",
      claimId: "Urn",
      claim: "EvidenceClaim",
    },

    /** A tombstone with reason — discarded claims never resurface as proof. */
    ClaimDiscardedPayload: {
      "+": "reject",
      owner: "UserId",
      claimId: "Urn",
      "reason?": "string",
    },

    /** A recorded downgrade `evidenced → self-positioning` when a quoted span stops
     *  resolving — structural anti-fabrication (domain-model.md §3.1). */
    ClaimSourceResolutionFailedPayload: {
      "+": "reject",
      owner: "UserId",
      claimId: "Urn",
      /** The grade the claim was downgraded to (`self-positioning`). */
      downgradedTo: "EvidenceGrade",
      "reason?": "string",
    },
  },

  resources: {
    ledger: {
      description: "The calling user's full evidence ledger",
      type: "EvidenceLedger",
      urnPattern: "urn:career:ledger:{userId}",
      fetch: { method: "GET", urlTemplate: "/api/v1/users/{userId}/evidence" },
      authz: { visibility: "user-owned", scopes: ["evidence:read"] },
    },
  },

  eventStream: {
    streamPattern: "Agg::career::{userId}::Evidence",
    events: {
      "claim.Proposed": {
        description:
          "The canonical claim write — a graded claim with sources + producer provenance (status: proposed)",
        type: "ClaimProposedPayload",
      },
      "claim.Confirmed": {
        description: "The owner confirmed a proposed claim — it may now be cited per its grade",
        type: "ClaimConfirmedPayload",
      },
      "claim.Edited": {
        description: "A claim was revised; grade re-derived (an edit can downgrade, never silently upgrade)",
        type: "ClaimEditedPayload",
      },
      "claim.Discarded": {
        description: "A claim was discarded (tombstone with reason); discarded claims never resurface as proof",
        type: "ClaimDiscardedPayload",
      },
      "claim.SourceResolutionFailed": {
        description: "A quoted span stopped resolving — recorded downgrade evidenced → self-positioning",
        type: "ClaimSourceResolutionFailedPayload",
      },
    },
  },

  authz: {
    scopes: {
      "evidence:read": { description: "Read the owner's evidence ledger" },
      "evidence:write": { description: "Propose, confirm, edit, or discard claims" },
    },
    policies: {
      "owner-only": {
        kind: "owner",
        description: "Every claim is owner-scoped to the calling Subject (data isolation, law C1)",
      },
    },
    defaults: {
      unauthenticatedAccess: "deny",
      missingRolesBehavior: "deny",
    },
  },
} as const satisfies DomainDefinitionJSON

export default evidenceDomain
